import { useMemo, useState } from "react";
import Icon from "./Icon";
import Drawer from "./Drawer";
import TodoList from "./TodoList";
import LoadingBlock from "./LoadingBlock";
import { useApi } from "../hooks/useApi";
import { listTodos, updateTodo } from "../api/todoService";
import { formatDate, daysFromToday } from "../utils/formatters";
import { showApiError } from "../utils/toast";

// Dashboard side rail: the next few open todos with a working done-tick, plus a
// "View all" drawer hosting the full TodoList (add / edit / delete / toggle).
// The Mine/Team switch mirrors the tabs on /admin/todos — `scope: "all"` is
// admin-only on the backend, and this card only ever renders on /admin.
export default function DashboardTodosCard({ limit = 5 }) {
  const [scope, setScope] = useState("mine"); // mine | team
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const isTeam = scope === "team";
  const params = useMemo(() => (isTeam ? { scope: "all" } : {}), [isTeam]);

  // GET /todos returns a plain array (not a paginator), so read it directly
  // rather than through paginatedRows.
  const todos = useApi(() => listTodos(params), [params]);
  const rows = useMemo(
    () => (Array.isArray(todos.data?.data) ? todos.data.data : []),
    [todos.data],
  );

  const active = useMemo(() => rows.filter((t) => !t.is_done), [rows]);
  // The backend already sorts by is_done, then due date (nulls last), then id
  // desc — so the first few are already the most pressing.
  const preview = active.slice(0, limit);
  const overflow = active.length - preview.length;

  const complete = async (todo) => {
    setBusyId(todo.id);
    try {
      await updateTodo(todo.id, { is_done: true });
      todos.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column" }}>
      {/* The rail is only 320px — let the title and the Mine/Team chips drop to
          a second line rather than squeezing each other. */}
      <div className="card-head" style={{ flexWrap: "wrap", rowGap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="card-title">Todos</div>
          <div className="card-sub">
            {active.length} open · {rows.length - active.length} done
          </div>
        </div>
        <div className="row gap-sm">
          {[
            { id: "mine", label: "Mine" },
            { id: "team", label: "Team" },
          ].map((t) => (
            <button
              key={t.id}
              className={`chip ${scope === t.id ? "active" : ""}`}
              onClick={() => setScope(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card-body no-pad" style={{ flex: 1 }}>
        {todos.loading ? (
          <div style={{ padding: 16 }}><LoadingBlock rows={3} height={28} /></div>
        ) : preview.length === 0 ? (
          <div style={{ padding: "20px 18px", fontSize: 13, color: "var(--ink-500)" }}>
            {isTeam ? "Nothing open across the team." : "Nothing on your list."}
          </div>
        ) : (
          preview.map((t) => {
            const overdue = t.due_date && daysFromToday(t.due_date) < 0;
            return (
              <div
                key={t.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "9px 18px",
                  borderTop: "1px solid var(--border)",
                }}
              >
                <input
                  type="checkbox"
                  checked={false}
                  disabled={busyId === t.id}
                  onChange={() => complete(t)}
                  title="Mark done"
                  style={{ marginTop: 3, cursor: "pointer", flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="t-primary"
                    style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {t.title}
                  </div>
                  <div className="row gap-sm" style={{ marginTop: 3, flexWrap: "wrap" }}>
                    {isTeam && t.user && (
                      <span className="badge badge-neutral" title="Owner">
                        <Icon name="user" size={10} /> {t.user.name}
                      </span>
                    )}
                    {t.due_date && (
                      <span className={`badge ${overdue ? "badge-danger" : "badge-neutral"}`}>
                        <Icon name="calendar" size={10} /> {formatDate(t.due_date)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          borderTop: "1px solid var(--border)",
          background: "var(--surface-2)",
        }}
      >
        <span className="t-muted text-xs" style={{ flex: 1 }}>
          {overflow > 0 ? `+${overflow} more` : `${active.length} open`}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
          View all <Icon name="arrow-right" size={12} />
        </button>
      </div>

      {/* Drawer returns null while closed, so TodoList unmounts and refetches on
          every open — the single reload here resyncs this compact card. */}
      <Drawer
        open={open}
        onClose={() => { setOpen(false); todos.reload(); }}
        wide
        title="Todos"
        subtitle={isTeam ? "Everyone's lists" : "Your checklist"}
      >
        <TodoList
          key={scope}
          params={params}
          canAdd={!isTeam}
          showOwner={isTeam}
          emptyHint={isTeam ? "No todos across the team yet." : undefined}
        />
      </Drawer>
    </div>
  );
}
